

import { useState, useRef } from "react"

const FileUpload = ({
  onFileSelect,
  acceptedTypes = ".pdf,.doc,.docx,.txt,.html,.md",
  maxSize = 50 * 1024 * 1024, // 50MB
  multiple = false,
}) => {
  const [dragActive, setDragActive] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState("")
  const fileInputRef = useRef(null)

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelection(e.dataTransfer.files[0])
    }
  }

  const handleFileSelection = (file) => {
    setError("")

    // Validate file type
    const allowedTypes = acceptedTypes.split(",").map((type) => type.trim())
    const fileExtension = "." + file.name.split(".").pop().toLowerCase()

    if (!allowedTypes.includes(fileExtension)) {
      setError(`File type not allowed. Accepted types: ${acceptedTypes}`)
      return
    }

    // Validate file size
    if (file.size > maxSize) {
      setError(`File size too large. Maximum size: ${formatFileSize(maxSize)}`)
      return
    }

    setSelectedFile(file)
    onFileSelect(file)
  }

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelection(e.target.files[0])
    }
  }

  const removeFile = () => {
    setSelectedFile(null)
    setError("")
    onFileSelect(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase()
    const iconMap = {
      pdf: "fas fa-file-pdf text-danger",
      doc: "fas fa-file-word text-primary",
      docx: "fas fa-file-word text-primary",
      txt: "fas fa-file-alt text-secondary",
      html: "fas fa-file-code text-warning",
      md: "fab fa-markdown text-dark",
    }
    return iconMap[extension] || "fas fa-file text-muted"
  }

  return (
    <div className="file-upload-component">
      {!selectedFile ? (
        <div
          className={`file-upload-area border-2 border-dashed rounded p-4 text-center ${
            dragActive ? "border-primary bg-light" : "border-secondary"
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{ cursor: "pointer", minHeight: "150px" }}
        >
          <div className="d-flex flex-column align-items-center justify-content-center h-100">
            <i className="fas fa-cloud-upload-alt fa-3x text-muted mb-3"></i>
            <h5>Drop your file here or click to browse</h5>
            <p className="text-muted mb-2">Accepted formats: {acceptedTypes}</p>
            <p className="text-muted small">Maximum file size: {formatFileSize(maxSize)}</p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedTypes}
            onChange={handleFileInputChange}
            style={{ display: "none" }}
            multiple={multiple}
          />
        </div>
      ) : (
        <div className="selected-file-display border rounded p-3">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <i className={`${getFileIcon(selectedFile.name)} fa-2x me-3`}></i>
              <div>
                <h6 className="mb-1">{selectedFile.name}</h6>
                <small className="text-muted">
                  {formatFileSize(selectedFile.size)} • {selectedFile.type || "Unknown type"}
                </small>
              </div>
            </div>
            <button type="button" className="btn btn-outline-danger btn-sm" onClick={removeFile}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="mt-3">
              <div className="progress">
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${uploadProgress}%` }}
                  aria-valuenow={uploadProgress}
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  {uploadProgress}%
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="alert alert-danger mt-2 mb-0">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}
    </div>
  )
}

export default FileUpload
